import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Waveform, GoogleLogo, ChartLine, Bell, Users, ShieldCheck, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Ambient "live" number that gently drifts to feel alive
  const [n, setN] = useState(128);
  useEffect(() => {
    const id = setInterval(() => setN((prev) => {
      const drift = (Math.random() - 0.5) * 6;
      return Math.max(72, Math.min(195, Math.round(prev + drift)));
    }), 2200);
    return () => clearInterval(id);
  }, []);

  const status = n < 70 ? "low" : n > 180 ? "high" : "in";
  const statusColor = status === "low" ? "text-red-600" : status === "high" ? "text-yellow-500" : "text-emerald-600";
  const statusLabel = status === "low" ? "LOW" : status === "high" ? "HIGH" : "IN RANGE";
  const statusDotBg = status === "low" ? "bg-red-600" : status === "high" ? "bg-yellow-500" : "bg-emerald-600";

  return (
    <div className="min-h-screen bg-white text-black relative overflow-hidden">
      {/* subtle dotted grid backdrop */}
      <div
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(0,0,0,0.055) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />
      <div className="absolute inset-x-0 top-0 h-96 -z-10 bg-gradient-to-b from-white via-white to-transparent" />

      {/* Nav */}
      <header className="max-w-[1400px] mx-auto px-6 py-6 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5" data-testid="landing-brand">
          <div className="w-9 h-9 rounded-md bg-black text-white grid place-items-center">
            <Waveform size={20} weight="duotone" />
          </div>
          <span className="font-display font-semibold text-xl tracking-tight">GlucoCloud</span>
        </a>
        <div className="flex items-center gap-6">
          <a href="#features" className="hidden sm:inline text-sm text-neutral-600 hover:text-black transition-colors">Features</a>
          <a href="#how" className="hidden sm:inline text-sm text-neutral-600 hover:text-black transition-colors">How it works</a>
          <Button
            onClick={handleLogin}
            data-testid="landing-login-top"
            className="h-9 rounded-md bg-black text-white hover:bg-neutral-800 gap-1.5 text-sm"
          >
            Sign in <ArrowUpRight size={14} weight="bold" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="max-w-[1400px] mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center">
        <div className="animate-fade-in">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-neutral-300 bg-white text-xs uppercase tracking-[0.2em] text-neutral-600 mb-8">
            <span className={`w-1.5 h-1.5 rounded-full ${statusDotBg} pulse-dot`} /> Open cloud CGM
          </div>
          <h1 className="font-display font-light text-6xl lg:text-[5.5rem] tracking-[-0.03em] leading-[0.92]">
            Every reading.<br />
            <span className="italic">Every day.</span><br />
            <span className="underline decoration-2 underline-offset-8 decoration-black">In one place.</span>
          </h1>
          <p className="mt-8 text-lg text-neutral-700 max-w-xl leading-relaxed">
            A personal CGM cloud you own. Upload from any
            device, watch in real time, review any historical day, share with
            caregivers — all in a clean black-and-white workspace.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              onClick={handleLogin}
              data-testid="landing-login-primary"
              className="h-12 px-6 rounded-md bg-black text-white hover:bg-neutral-800 gap-2 font-medium text-base"
            >
              <GoogleLogo size={18} weight="bold" /> Continue with Google
              <ArrowRight size={16} weight="bold" className="ml-1" />
            </Button>
            <a
              href="#how"
              className="h-12 px-6 rounded-md border border-black grid place-items-center text-sm hover:bg-black hover:text-white transition-colors"
              data-testid="landing-how-link"
            >
              See how it works
            </a>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-neutral-500 uppercase tracking-[0.18em]">
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} /> Your data · your account</span>
            <span className="hidden sm:inline">·</span>
            <span>mg/dL &amp; mmol/L</span>
            <span className="hidden sm:inline">·</span>
            <span>xDrip+ · Vitatok · Spike compatible</span>
          </div>
        </div>

        {/* Preview device */}
        <div className="relative animate-fade-in">
          <div className="relative rounded-2xl bg-black text-white p-8 shadow-2xl">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.24em] text-neutral-400 mb-8">
              <span className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${statusDotBg} pulse-dot`} /> {statusLabel}
              </span>
              <span className="tabular">LIVE · {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>

            <div className="flex items-end gap-4">
              <div className={`font-display font-extralight text-[8rem] leading-none tabular ${statusColor}`}>
                {n}
              </div>
              <div className={`pb-8 text-6xl font-display ${statusColor}`}>→</div>
            </div>
            <div className="text-sm text-neutral-400 mt-2 tabular">mg/dL · <span className="text-neutral-300">just now</span></div>

            {/* mini bars */}
            <div className="mt-10 h-16 flex items-end gap-1">
              {Array.from({ length: 40 }).map((_, i) => {
                const v = 100 + Math.sin(i / 3) * 45 + (i > 30 ? (i - 30) * 4 : 0);
                const h = Math.max(6, Math.min(64, ((v - 40) / 360) * 64));
                const color = v > 180 ? "bg-yellow-500" : v < 70 ? "bg-red-500" : "bg-emerald-500";
                return <div key={i} className={`w-1.5 rounded-sm ${color}`} style={{ height: `${h}px` }} />;
              })}
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 pt-6 border-t border-white/10">
              <MiniStat label="TIR" value="82%" tone="emerald" />
              <MiniStat label="GMI" value="6.1" tone="neutral" />
              <MiniStat label="AVG" value="132" tone="neutral" />
            </div>
          </div>
          {/* corner ticks */}
          <span className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-black" />
          <span className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-black" />
          <span className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-black" />
          <span className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-black" />
        </div>
      </section>

      {/* Bar / stats */}
      <section className="border-y border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "Retention", v: "90 days", d: "Full history, all zoom levels" },
            { k: "Refresh", v: "60s", d: "Auto-updating live view" },
            { k: "Devices", v: "Any CGM", d: "Compatible with all major uploaders" },
            { k: "Users", v: "Multi-patient", d: "Caregiver-friendly" },
          ].map((s) => (
            <div key={s.k}>
              <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">{s.k}</div>
              <div className="font-display text-2xl mt-1 tabular">{s.v}</div>
              <div className="text-xs text-neutral-600 mt-0.5">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <div className="text-xs uppercase tracking-[0.24em] text-neutral-500 mb-4">What&apos;s inside</div>
          <h2 className="font-display font-light text-4xl lg:text-5xl tracking-[-0.02em]">
            Everything you need to manage your CGM &mdash; only <em>quieter</em>.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ChartLine, title: "Live + historical", desc: "3h · 6h · 12h · 24h · 7d · 30d. Pick any specific date and zoom into that day." },
            { icon: Bell, title: "Threshold alerts", desc: "Set urgent low & high. Line turns red under low, yellow over high, green in-between." },
            { icon: Users, title: "Followers", desc: "Caregivers add patients by email and watch their live glucose safely." },
            { icon: ShieldCheck, title: "Device API", desc: "Drop-in for xDrip+, Vitatok, AndroidAPS. Secure SHA-1 secret header." },
          ].map((f) => (
            <div key={f.title} className="p-6 border border-black rounded-md bg-white hover:bg-black hover:text-white group transition-colors">
              <f.icon size={22} weight="duotone" className="mb-5 text-black group-hover:text-white transition-colors" />
              <div className="font-display font-medium text-lg mb-2 tracking-tight">{f.title}</div>
              <div className="text-sm text-neutral-600 group-hover:text-neutral-300 leading-relaxed transition-colors">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-neutral-500 mb-4">Three steps</div>
            <h2 className="font-display font-light text-4xl lg:text-5xl tracking-[-0.02em] mb-8">
              Sign in. Point your uploader. Done.
            </h2>
            <p className="text-neutral-700 leading-relaxed max-w-md">
              No servers to spin up, no docker containers to babysit. Sign in
              with Google, copy your GlucoCloud URL &amp; API secret from
              Settings, paste them into xDrip+ or Vitatok, and your readings
              flow straight into your cloud.
            </p>
          </div>
          <ol className="space-y-8">
            {[
              { n: "01", t: "Sign in with Google", d: "One click. No passwords. Your session is a secure httpOnly cookie." },
              { n: "02", t: "Set your API secret", d: "Any passphrase ≥ 12 chars. Stored only as SHA-1 hex. Works with every major CGM uploader." },
              { n: "03", t: "Watch it flow", d: "See live values in seconds. Scroll back through 30 days or pick any specific date." },
            ].map((s) => (
              <li key={s.n} className="flex gap-6 pb-8 border-b border-black/10 last:border-0 last:pb-0">
                <div className="font-display text-3xl font-light text-neutral-400 tabular">{s.n}</div>
                <div>
                  <div className="font-display text-xl mb-1">{s.t}</div>
                  <div className="text-sm text-neutral-600 leading-relaxed">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-24 text-center">
          <h2 className="font-display font-light text-5xl lg:text-6xl tracking-[-0.03em] max-w-3xl mx-auto">
            Your CGM data. In the cloud you own.
          </h2>
          <Button
            size="lg"
            onClick={handleLogin}
            data-testid="landing-login-bottom"
            className="mt-10 h-13 px-8 rounded-md bg-black text-white hover:bg-neutral-800 gap-2 font-medium text-base"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
            <ArrowRight size={16} weight="bold" className="ml-1" />
          </Button>
          <div className="mt-8 text-xs text-neutral-500 uppercase tracking-[0.18em]">Free forever · No credit card</div>
        </div>
      </section>

      <footer className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <span>GlucoCloud &middot; Personal cloud for CGM data</span>
          <span>Not a medical device. For information only.</span>
        </div>
      </footer>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const color = tone === "emerald" ? "text-emerald-400" : "text-white";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">{label}</div>
      <div className={`font-display text-xl tabular mt-1 ${color}`}>{value}</div>
    </div>
  );
}
